import React, { useState, useEffect, useCallback } from 'react';
import { Star, Trash2, ChevronRight, RefreshCw, Sparkles, MessageSquare } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';

/**
 * MyRecipesView
 * -------------
 * Lists the user's saved (favourited) AI-Chef recipes, with per-meal feedback
 * (tasty / simple / would-cook-again / notes).  Lives as a tab inside the
 * Diet tab — sits beside Browse / Gallery / My plan.
 *
 * Props:
 *   onOpenMeal(meal)  — open the full recipe in the existing MealDetailModal
 *   onRemove(mealId)  — caller's hook so the parent can keep its savedRecipeIds in sync
 */
export default function MyRecipesView({ onOpenMeal, onRemove }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${apiBase}/api/user/my-recipes`);
      if (r.ok) {
        const d = await r.json();
        setRecipes(d.recipes || []);
      }
    } catch (_) { /* swallow */ }
    setLoading(false);
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  const updateFeedback = async (mealId, patch) => {
    try {
      const r = await authFetch(`${apiBase}/api/user/my-recipes/${encodeURIComponent(mealId)}/feedback`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const d = await r.json();
        setRecipes(prev => prev.map(rec => rec.meal?.id === mealId ? { ...rec, feedback: d.feedback } : rec));
      }
    } catch (_) {
      toast.error('Could not save feedback');
    }
  };

  const removeRecipe = async (mealId) => {
    try {
      const r = await authFetch(`${apiBase}/api/user/my-recipes/${encodeURIComponent(mealId)}`, { method: 'DELETE' });
      if (r.ok) {
        setRecipes(prev => prev.filter(rec => rec.meal?.id !== mealId));
        if (onRemove) onRemove(mealId);
        toast.message('Removed from My Recipes');
      }
    } catch (_) {
      toast.error('Could not remove');
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center text-sm text-zinc-500" data-testid="my-recipes-loading">
        <RefreshCw className="w-5 h-5 inline-block animate-spin mr-2" />
        Loading your recipes…
      </div>
    );
  }

  if (recipes.length === 0) {
    return (
      <div className="bg-white border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center" data-testid="my-recipes-empty">
        <Sparkles className="w-8 h-8 text-violet-300 mx-auto mb-3" />
        <h3 className="text-sm font-bold text-zinc-700 mb-1">No saved recipes yet</h3>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Tap the <Star className="w-3 h-3 inline align-baseline" /> Save button on any AI Chef recipe to keep it here for your 2-week plan and beyond.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="my-recipes-list">
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 flex items-center gap-2.5">
        <Sparkles className="w-4 h-4 text-violet-700 flex-shrink-0" />
        <p className="text-[11px] text-violet-900 leading-relaxed">
          Your favourited recipes from AI Chef.  Rate each one as you cook them so Tudor and the chef learn what works for you.
        </p>
      </div>

      {recipes.map((rec) => (
        <RecipeCard
          key={rec.meal?.id}
          rec={rec}
          onOpen={() => onOpenMeal && onOpenMeal(rec.meal)}
          onRemove={() => removeRecipe(rec.meal?.id)}
          onFeedback={(patch) => updateFeedback(rec.meal?.id, patch)}
        />
      ))}
    </div>
  );
}

function StarRow({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          onClick={(e) => { e.stopPropagation(); onChange(n); }}
          className="p-0.5 hover:scale-110 transition-transform"
          data-testid={`${testId}-${n}`}
          aria-label={`Rate ${n}`}
        >
          <Star className={`w-4 h-4 ${n <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-zinc-300'}`} />
        </button>
      ))}
    </div>
  );
}

function RecipeCard({ rec, onOpen, onRemove, onFeedback }) {
  const meal = rec.meal || {};
  const feedback = rec.feedback || {};
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(feedback.notes || '');

  const saveNotes = () => {
    onFeedback({ notes: notesDraft });
    setEditingNotes(false);
  };

  return (
    <div className="bg-white border-2 border-violet-100 rounded-xl p-3" data-testid={`my-recipe-${meal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={onOpen} className="flex-1 text-left" data-testid={`my-recipe-open-${meal.id}`}>
          <h4 className="text-sm font-bold text-zinc-900">{meal.title || 'Untitled recipe'}</h4>
          <div className="text-[10px] text-zinc-500 mt-0.5">
            <span className="capitalize">{meal.category || 'meal'}</span>
            {meal.country && <> · {meal.country}</>}
            {(meal.prep_minutes != null || meal.cook_minutes != null) && (
              <> · {(meal.prep_minutes || 0) + (meal.cook_minutes || 0)} min</>
            )}
            {meal.difficulty && <> · {meal.difficulty}</>}
          </div>
        </button>
        <button
          onClick={onRemove}
          className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
          aria-label="Remove from favourites"
          data-testid={`my-recipe-remove-${meal.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Feedback rows: tasty + simple + would-cook-again */}
      <div className="mt-3 space-y-1.5 border-t border-zinc-100 pt-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-zinc-600">Tasty?</span>
          <StarRow value={feedback.tasty} onChange={(v) => onFeedback({ tasty: v })} testId={`feedback-tasty-${meal.id}`} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-zinc-600">Simple to prepare?</span>
          <StarRow value={feedback.simple} onChange={(v) => onFeedback({ simple: v })} testId={`feedback-simple-${meal.id}`} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-zinc-600">Cook again?</span>
          <div className="flex gap-1">
            <button
              onClick={() => onFeedback({ would_cook_again: true })}
              className={`text-[10px] font-bold px-2 py-1 rounded-md transition-colors ${
                feedback.would_cook_again === true
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                  : 'bg-zinc-50 text-zinc-500 border border-zinc-200 hover:bg-emerald-50'
              }`}
              data-testid={`feedback-cookagain-yes-${meal.id}`}
            >Yes</button>
            <button
              onClick={() => onFeedback({ would_cook_again: false })}
              className={`text-[10px] font-bold px-2 py-1 rounded-md transition-colors ${
                feedback.would_cook_again === false
                  ? 'bg-zinc-200 text-zinc-700 border border-zinc-300'
                  : 'bg-zinc-50 text-zinc-500 border border-zinc-200 hover:bg-zinc-100'
              }`}
              data-testid={`feedback-cookagain-no-${meal.id}`}
            >No</button>
          </div>
        </div>

        {/* Notes (collapsible) */}
        {!editingNotes ? (
          <button
            onClick={() => { setNotesDraft(feedback.notes || ''); setEditingNotes(true); }}
            className="w-full text-left mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500 hover:text-zinc-700"
            data-testid={`feedback-notes-edit-${meal.id}`}
          >
            <MessageSquare className="w-3 h-3" />
            {feedback.notes ? `Note: "${feedback.notes.slice(0, 60)}${feedback.notes.length > 60 ? '…' : ''}"` : 'Add a note…'}
          </button>
        ) : (
          <div className="mt-1">
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              maxLength={500}
              placeholder="e.g. doubled the garlic, used wholemeal pasta: kept me full till bedtime"
              className="w-full text-xs p-2 border border-zinc-200 rounded-md focus:border-violet-400 focus:outline-none"
              rows={2}
              data-testid={`feedback-notes-textarea-${meal.id}`}
            />
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={saveNotes}
                className="flex-1 text-[10px] font-bold uppercase tracking-wider bg-violet-600 text-white py-1.5 rounded-md hover:bg-violet-700"
                data-testid={`feedback-notes-save-${meal.id}`}
              >Save note</button>
              <button
                onClick={() => setEditingNotes(false)}
                className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 px-2"
                data-testid={`feedback-notes-cancel-${meal.id}`}
              >Cancel</button>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={onOpen}
        className="w-full text-[11px] font-semibold text-violet-600 hover:text-violet-800 mt-2 inline-flex items-center justify-end gap-0.5"
        data-testid={`my-recipe-view-${meal.id}`}
      >
        View full recipe <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
}
